import { Add, Delete, Edit } from "@mui/icons-material";
import { Button, Chip, Grid, Typography } from "@mui/material";
import { useLookup } from "context/lookupContext";
import { format, parseISO } from "date-fns";
import "font-awesome/css/font-awesome.min.css";
import { useYupValidationResolver } from "hooks/useYupValidationResolver";
import { getAgeAtYearEnd } from "qubCommon/helperMethods/getAgeAtYearEnd";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useChildValidation } from "util/validations/child-form";
import ChildFormDialog from "./ChildFormDialog";

const decisionStatusPresentation = {
  900: { label: "Δικαιούχος χωρίς κλήρωση", color: "success" },
  905: { label: "Προς κλήρωση", color: "warning" },
  910: { label: "Κληρώθηκε", color: "success" },
  920: { label: "Δεν κληρώθηκε", color: "default" },
  930: { label: "Προς πληρωμή (ΔΙΑΣ)", color: "info" },
  940: { label: "Αποτυχία πληρωμής", color: "error" },
  950: { label: "Επαναπληρωμή", color: "warning" },
  960: { label: "Ολοκλήρωση καταβολής", color: "success" },
};

const ChildrenTable = ({
  children,
  canAdd = false,
  canDelete = false,
  canEdit = false,
  canPartiallyEdit = false,
  programYear,
  applicantIsOrphanInSeasonWhitelist,
}) => {
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const childValidation = useChildValidation();
  const resolver = useYupValidationResolver(childValidation);
  const childForm = useForm({ resolver });
  const form = useFormContext();
  const insuredToOptions = form?.watch("husbandsHelp") ?? [];
  const handleCloseDialog = () => {
    childForm.reset({});
    setOpenDialog(false);
  };
  const { lookups } = useLookup();
  const showDecisionStatus = data?.some((child) => child?.decisionStatusId);

  const applicantAmka = form.getValues("applicant")?.amka || "";

  useEffect(() => {
    setData(
      children
      // children?.map((child) => {
      //   if (!isAdult(child?.dob?.datePickerFormatted) && !child?.category?.id) {
      //     const selectedObject = lookups?.childCategories?.find(
      //       (record) => record?.id === 1
      //     );
      //     console.log(selectedObject?.fileDataValue);
      //     return {
      //       ...child,
      //       category: {
      //         id: 1,
      //         name: "Ανήλικο",
      //         fileDataKey: selectedObject?.fileDataKey,
      //         fileDataValue: selectedObject?.fileDataValue,
      //       },
      //     };
      //   } else return child;
      // })
    );
  }, [children]);

  useEffect(() => {
    form.setValue("childrenHelp", children, {
      shouldDirty: false,
    });
    form.setValue("children", null, { shouldDirty: false });
    form.setValue("children.created", [], { shouldDirty: false });
    form.setValue("children.updated", [], { shouldDirty: false });
    form.setValue("children.deleted", [], { shouldDirty: false });
  }, [children]);

  const sortTable = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    let sortedData;
    if (["gender", "category"].includes(key)) {
      sortedData = [...data].sort((a, b) => {
        const aValue = a[key]?.name || "";
        const bValue = b[key]?.name || "";
        if (aValue < bValue) {
          return direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    } else if (["dob"].includes(key)) {
      sortedData = [...data].sort((a, b) => {
        const aValue = a[key]?.dateShortFormatted || "";
        const bValue = b[key]?.dateShortFormatted || "";
        if (aValue < bValue) {
          return direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    } else {
      sortedData = [...data].sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    setData(sortedData);
  };

  const getIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        return <i className="fas fa-sort-up"></i>;
      } else {
        return <i className="fas fa-sort-down"></i>;
      }
    } else {
      return <i className="fas fa-sort"></i>;
    }
  };

  const handleAddRow = async (child) => {
    form.setValue("childrenHelp", [...data, child], {
      shouldDirty: true,
    });
    setData((prev) => [...prev, { ...child, position: prev?.length + 1 }]);
    setOpenDialog(false);
    childForm.reset({});
    const createdChildren = form.getValues("children.created") ?? [];
    form.setValue("children.created", [...createdChildren, child], {
      shouldDirty: true,
    });
  };

  const handleBeginEdit = (child) => {
    childForm.reset(child);
    setOpenDialog(true);
  };

  const handleDeleteRow = (child) => {
    if (
      window.confirm(
        `Είστε σίγουροι οτι θέλετε να διαγράψετε το μέλος #${child?.position} της ομάδας`
      )
    ) {
      if (child?.isNew) {
        const createdChildren = form.getValues("children.created") ?? [
          form.setValue(
            "children.created",
            createdChildren.filter((emp) => emp.id !== child.id),
            { shouldDirty: true }
          ),
        ];
      } else {
        const updatedChildren = form.getValues("children.updated");
        form.setValue(
          "children.updated",
          updatedChildren.filter((ap) => ap.id !== child.id),
          { shouldDirty: true }
        );
        const deletedChildren = form.getValues("children.deleted") ?? [];
        form.setValue("children.deleted", [...deletedChildren, child?.id], {
          shouldDirty: true,
        });
      }
      setData((prev) => {
        let returnData = prev;
        returnData = prev.filter((row) => row.id !== child.id);
        form.setValue("childrenHelp", returnData, {
          shouldDirty: false,
        });
        return returnData.map((row) =>
          row?.position > child?.position
            ? { ...row, position: row?.position - 1 }
            : row
        );
      });
    }
  };

  const handleEditRow = async (child) => {
    form.setValue(
      "childrenHelp",
      data?.map((row) => (row.id === child.id ? { ...row, ...child } : row))
    );
    setData((prev) =>
      prev.map((row) => (row.id === child.id ? { ...row, ...child } : row))
    );

    const editedChildren = form
      .getValues("children.updated")
      .filter((ap) => ap.id !== child.id);

    if (child?.isNew) {
      const createdChildren =
        form
          .getValues("children.created")
          ?.filter((ap) => ap.id !== child.id) ?? [];
      form.setValue("children.created", [...createdChildren, child], {
        shouldDirty: true,
      });
    } else
      form.setValue("children.updated", [...editedChildren, child], {
        shouldDirty: true,
      });
    setOpenDialog(false);
    childForm.reset({});
  };

  const husbands = form.watch("husbandsHelp") || [];
  const guardianOptions =
    [form.getValues("applicant"), ...husbands]?.map((i) => ({
      ...i,
      fullName: `${i?.lastName ?? ""} ${i?.firstName ?? ""} (${i?.amka})`,
    })) || [];

  let amkaOptions;
  try {
    amkaOptions =
      [form.getValues("applicant"), ...husbands]?.map((i) => i.amka) || [];
  } catch (e) {
    amkaOptions = [];
  }
  const getGuardianName = (id) => {
    const guardian = guardianOptions.find((g) => g.id === id);
    return guardian ? guardian.fullName : "";
  };

  const formatDate = (date) => {
    try {
      return format(parseISO(date), "dd/MM/yyyy");
    } catch (e) {
      return "";
    }
  };

  return (
    <>
      <ChildFormDialog
        open={openDialog}
        handleAddRow={handleAddRow}
        handleClose={handleCloseDialog}
        childForm={childForm}
        handleEditRow={handleEditRow}
        canEdit={canEdit}
        canPartiallyEdit={canPartiallyEdit}
        guardianOptions={guardianOptions}
        applicantAmka={applicantAmka}
        applicantIsOrphanInSeasonWhitelist={applicantIsOrphanInSeasonWhitelist}
        amkaOptions={amkaOptions}
        programYear={programYear}
      />

      {canAdd && (
        <Grid container item xs={12} sx={{ paddingBottom: 2 }}>
          <Button
            sx={{ marginLeft: "auto" }}
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              childForm.reset();
              setOpenDialog(true);
            }}
          >
            Προσθήκη
          </Button>
        </Grid>
      )}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table className="application-input-table">
          <thead>
            <tr>
              <th align="center" onClick={() => sortTable("position")}>
                Α/Α {getIcon("position")}
              </th>
              <th onClick={() => sortTable("amka")}>ΑΜΚΑ {getIcon("amka")}</th>
              <th onClick={() => sortTable("lastName")}>
                Ονοματεπώνυμο {getIcon("lastName")}
              </th>
              {/* <th onClick={() => sortTable("lastName")}>
                Επώνυμο {getIcon("lastName")}
              </th>
              <th onClick={() => sortTable("firstName")}>
                Όνομα {getIcon("firstName")}
              </th> */}

              <th onClick={() => sortTable("gender")}>
                Φύλο {getIcon("gender")}
              </th>
              <th onClick={() => sortTable("dob")}>
                Ημ/νία Γέν. {getIcon("dob")}
              </th>
              <th onClick={() => sortTable("insuranceRelationship")}>
                Έμμεσα Ασφαλισμένο σε {getIcon("insuranceRelationship")}
              </th>
              <th onClick={() => sortTable("childStatus")}>
                Ιδιότητα {getIcon("childStatus")}
              </th>
              {showDecisionStatus && <th>Κατάσταση ωφελούμενου</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.map((row, index) => {
              let age;
              if (row?.dob?.datePickerFormatted) {
                age = getAgeAtYearEnd(
                  row?.dob?.datePickerFormatted,
                  programYear
                );
                if (age === 0) age = "0";
              }
              return (
                <tr key={index}>
                  <td align="center">{row?.position ?? ""}</td>
                  <td>{row?.amka ?? ""}</td>
                  <td align="">
                    {row?.lastName} {row?.firstName}
                  </td>

                  {/* <td>{row?.lastName ?? ""}</td>
                <td>{row?.firstName ?? ""}</td> */}
                  <td>{row?.gender?.name ?? ""}</td>
                  <td>
                    {formatDate(row?.dob?.datePickerFormatted)}{" "}
                    {age ? `(${age} ετών)` : ""}
                  </td>
                  <td>{getGuardianName(row?.guardianId)}</td>
                  <td>{row?.category?.name ?? <Typography color='error' fontWeight='bold'>Παρακαλώ επιλέξτε ιδιότητα!</Typography>}</td>

                  {showDecisionStatus && (
                    <td>
                      {row?.decisionStatusId && (
                        <Chip
                          size="small"
                          variant={row.decisionStatusId === 920 ? "outlined" : "filled"}
                          color={decisionStatusPresentation[row.decisionStatusId]?.color ?? "default"}
                          label={decisionStatusPresentation[row.decisionStatusId]?.label ?? `Κατάσταση ${row.decisionStatusId}`}
                          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                        />
                      )}
                    </td>
                  )}

                  <td>
                    <div className="action-buttons">
                      {(canEdit || canPartiallyEdit) && (
                        <button
                          onClick={() => handleBeginEdit(row)}
                          className="btn btn-edit-small"
                        >
                          <Edit fontSize="small" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteRow(row)}
                          className="btn btn-edit-small"
                        >
                          <Delete fontSize="small" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table >
      </div >
    </>
  );
};

export default ChildrenTable;
